import { useCallback, useEffect, useState, type RefObject } from "react";

/**
 * 게임 상세 「햅틱 이펙트」 카드 틀의 비율 — **섹션 안 영상 원본 비율 중 가장 세로로 긴 것** 하나로 통일한다.
 *
 * 종전엔 틀을 시안 비율(웹 220:140, 앱 웹뷰 1:1)로 고정하고 `<video>` 에 크기·맞춤을 주지 않아서,
 * 원본이 틀과 다르면 영상이 원본 크기로 그려진 뒤 **틀 밖이 잘렸다**. 실측(2026-10-15 prod `sdk-apis`
 * `by-filters` 403게임 전수): 카드 영상 19개(5게임)가 **전부 1080×1080** 이라 웹 카드(1.57:1)에서 아래쪽
 * 약 37% — 햅틱 비주얼라이저가 있는 자리 — 가 잘렸다. 업로드 비율을 약속으로 맞추는 대신 화면이 원본을 읽는다.
 *
 * - **섹션 통일**: 카드마다 자기 비율이면 같은 줄의 카드 높이가 들쭉날쭉하다. 가장 세로로 긴 비율로
 *   맞추면 어느 영상도 잘리지 않고(나머지는 `object-contain` 여백) 그리드가 유지된다.
 * - **기본값 1:1**: 비율은 영상 메타데이터가 와야 안다. 그 전(SSR·로딩 중)은 지금 데이터 전부인 1:1 로
 *   틀을 잡아 로드 뒤 높이가 바뀌지 않게 한다. 근본적으로 없애려면 API 가 영상 크기를 줘야 한다.
 */
export const DEFAULT_EFFECT_MEDIA_ASPECT = 1;

/** `width / height` 목록 → 가장 세로로 긴(= 가장 작은) 비율. 유효한 값이 없으면 기본값. */
export const tallestAspect = (
  aspects: readonly number[],
  fallback: number = DEFAULT_EFFECT_MEDIA_ASPECT,
): number => {
  const valid = aspects.filter((a) => Number.isFinite(a) && a > 0);
  return valid.length > 0 ? Math.min(...valid) : fallback;
};

/** 카드 식별자 — 같은 카테고리라도 **영상·이미지가 다르면 다른 카드**다(아래 `useSectionMediaAspect` 주석). */
export const effectMediaKey = (e: {
  category: string;
  videoLink: string;
  imageLink: string;
}) => `${e.category}|${e.videoLink}|${e.imageLink}`;

/**
 * 섹션 단위 — 카드가 원본 비율을 보고하면 섹션 비율을 돌려준다.
 *
 * ★비율은 **지금 받은 카드(`keys`)의 보고만**으로 정한다. 게임 상세는 같은 동적 라우트라 「추천 게임」으로
 * 다른 게임에 가도 이 컴포넌트가 **다시 마운트되지 않는다**(호출처에 key 없음 · `_app` 도 경로 key 없음).
 * 보고를 누적만 하면 이전 게임 영상(예: 9:16)이 다음 게임(1:1) 섹션 비율을 계속 정한다(#6027 R1 재현).
 * 호출처 `key={game.id}` 로도 막히지만 그건 호출처마다 기억해야 하는 의무라, 여기서 닫는다 — 카드 쪽은
 * `effectMediaKey` 를 React key 로 써서 영상이 바뀌면 카드가 새로 마운트(스켈레톤·재생 상태 초기화)된다.
 */
export const useSectionMediaAspect = (keys: readonly string[]) => {
  const [aspects, setAspects] = useState<Record<string, number>>({});
  const report = useCallback((key: string, width: number, height: number) => {
    if (!(width > 0 && height > 0)) return;
    const aspect = width / height;
    setAspects((prev) =>
      prev[key] === aspect ? prev : { ...prev, [key]: aspect },
    );
  }, []);
  const current = keys.flatMap((k) => (k in aspects ? [aspects[k]] : []));
  return { aspect: tallestAspect(current), report };
};

/**
 * 카드 단위 — 영상의 원본 크기 보고 + 첫 프레임 준비 여부(스켈레톤용).
 *
 * ⚠️ 영상은 SSR 로 먼저 그려지고 `preload="auto"` 라 **하이드레이션 전에** `loadedmetadata`·`loadeddata`
 * 가 지나갈 수 있다(React 핸들러가 붙기 전이라 놓친다). 그래서 마운트 때 `readyState` 를 직접 읽는다.
 * 실패(`error`)도 「준비 끝」으로 친다 — 스켈레톤이 영원히 돌지 않게.
 */
export const useEffectVideo = (
  videoRef: RefObject<HTMLVideoElement | null>,
  onAspect: (width: number, height: number) => void,
) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const readMeta = () => onAspect(v.videoWidth, v.videoHeight);
    const markReady = () => setReady(true);
    // HAVE_METADATA = 1 · HAVE_CURRENT_DATA = 2
    if (v.readyState >= 1) readMeta();
    if (v.readyState >= 2 || v.error) markReady();
    v.addEventListener("loadedmetadata", readMeta);
    v.addEventListener("loadeddata", markReady);
    v.addEventListener("error", markReady);
    return () => {
      v.removeEventListener("loadedmetadata", readMeta);
      v.removeEventListener("loadeddata", markReady);
      v.removeEventListener("error", markReady);
    };
  }, [videoRef, onAspect]);

  return { ready };
};
